/**
 * Hamlet — „Unmatched: Slings & Arrows".
 *
 * THE QUESTION: na začiatku ťahu volí TO BE alebo NOT TO BE (verejné,
 * `players[i].form`: `be` / `notbe`). NOT TO BE: 2 poškodenia jednej vlastnej
 * figúrke a +2 k Hamletovým útokom. TO BE: pri manévri o kartu viac.
 */

import type { Ctx } from '../ops.js';
import {
  askChoice,
  cardDef,
  dealDamage,
  discardRandom,
  drawCard,
  emit,
  endTurnNow,
  gainAction,
  getMap,
  getPlayer,
  handToDeckBottom,
  healFighter,
  millDeck,
  returnFromDiscard,
} from '../ops.js';
import type { PlayerIndex } from '../types.js';
import { askBoost, askMove, emptySpacesInZone, fighterOrNull, opponentDiscards, opponentOf } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const HAMLET = 'hamlet';
const READINESS = 'hamlet.the-readiness-is-all';
const FORTUNE = 'hamlet.outrageous-fortune';
const METHOD = 'hamlet.method-in-the-madness';
const GHOST = 'hamlet.the-ghost';

const hamletOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === HAMLET) ?? null;

const theirCard = (e: EffectCtx) => {
  const combat = e.ctx.state.combat;
  if (!combat) return null;
  if (e.role === 'defender') return combat.bonus ? null : combat.attackCard;
  return combat.defenseCard;
};

/** Zahral súper univerzálnu kartu? */
const theyPlayedVersatile = (e: EffectCtx) => {
  const card = theirCard(e);
  return Boolean(card && cardDef(e.ctx, card).type === 'versatile');
};

const side = (e: EffectCtx) => (e.role === 'defender' ? 'defender' : 'attacker');

/** „You may BOOST this card." */
const cruelToBeKind: CardEffect = {
  duringChoice: (e) => askBoost(e, 'Cruel to Be Kind: smieš boostovať'),
};

/** „If your opponent played a versatile card, ignore its value." */
const uncertainDoom: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    if (combat && theyPlayedVersatile(e)) (combat.flags ??= []).push(e.role === 'defender' ? 'zeroAttack' : 'zeroDefense');
  },
};

/**
 * „If you won the combat, move the opposing fighter a number of spaces up to
 * the amount of combat damage dealt. If you lost the combat, Hamlet recovers 1 health."
 */
const toSleep: CardEffect = {
  after: (e) => {
    if (e.won) {
      if (e.opponentFighterId && e.damage) {
        askMove(e, { fighters: [e.opponentFighterId], max: e.damage, label: `To Sleep, Perchance to Dream: presuň súpera až o ${e.damage}` });
      }
      return;
    }
    const hamlet = hamletOf(e.ctx, e.player);
    if (hamlet) healFighter(e.ctx, hamlet.id, 1);
  },
};

/** „Place one of the fighters in the combat in any space. They cannot leave that space this turn." */
const readinessIsAll: CardEffect = {
  after: (e) => {
    const inCombat = [e.fighterId, e.opponentFighterId].filter((id): id is string => Boolean(id));
    if (inCombat.length === 0) return;
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'The Readiness Is All: koho položíš (do konca ťahu sa nepohne)?',
      optional: false,
      options: inCombat.map((id) => ({
        label: fighterOrNull(e.ctx, id)?.name ?? id,
        follow: { kind: 'effect' as const, effect: READINESS, data: `who:${id}` },
      })),
    });
  },
  onFollow: (ctx, choice, data) => {
    const [step, id] = data.split(':') as [string, string];
    if (step === 'pin') {
      if (fighterOrNull(ctx, id)) (ctx.state.turn.pinned ??= []).push(id);
      return;
    }
    const occupied = new Set(ctx.state.fighters.map((f) => f.spaceId));
    const spaces = getMap(ctx).spaces.map((s) => s.id).filter((s) => !occupied.has(s));
    if (spaces.length > 0) {
      askChoice(ctx, {
        kind: 'place',
        player: choice.player,
        source: choice.source,
        label: 'The Readiness Is All: polož figúrku na ľubovoľný priestor',
        optional: false,
        fighterId: id,
        spaces,
      });
    }
    askChoice(ctx, {
      kind: 'auto',
      player: choice.player,
      source: choice.source,
      label: 'The Readiness Is All: figúrka sa nepohne',
      optional: false,
      follow: { kind: 'effect', effect: READINESS, data: `pin:${id}` },
    });
  },
};

/** „Your opponent discards 1 random card. If you won the combat, Hamlet recovers health equal to its BOOST value." */
const playsTheThing: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null) return;
    const cardId = discardRandom(e.ctx, opponent, true);
    const hamlet = hamletOf(e.ctx, e.player);
    if (e.won && cardId && hamlet) healFighter(e.ctx, hamlet.id, cardDef(e.ctx, cardId).boost);
  },
};

/** „If Hamlet took damage this turn, deal 1 damage to the opposing fighter." */
const bloodWillHaveBlood: CardEffect = {
  after: (e) => {
    const hamlet = hamletOf(e.ctx, e.player);
    if (hamlet && e.ctx.state.turn.hurt?.includes(hamlet.id) && e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, 1);
  },
};

/**
 * „DURING: Discard the top card of your deck. Add its BOOST value to this
 * card's value. AFTER: If you lost the combat, return the card you discarded
 * to your hand."
 */
const outrageousFortune: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    const top = getPlayer(e.ctx.state, e.player).deck[0];
    if (!combat || !top) return;
    millDeck(e.ctx, e.player, 1);
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[side(e)] += cardDef(e.ctx, top).boost;
    (combat.flags ??= []).push(`fortune:${top}`);
  },
  after: (e) => {
    const flag = e.ctx.state.combat?.flags?.find((f) => f.startsWith('fortune:'));
    const cardId = flag?.slice('fortune:'.length);
    if (!e.won && cardId && getPlayer(e.ctx.state, e.player).discard.includes(cardId)) {
      returnFromDiscard(e.ctx, e.player, [cardId]);
    }
  },
};

/** „The value of this card is equal to the number of cards in your hand. AFTER: If you won the combat, your opponent discards 1 card." */
const maddeningInsight: CardEffect = {
  during: (e) => getPlayer(e.ctx.state, e.player).hand.length,
  after: (e) => {
    if (e.won) opponentDiscards(e, opponentOf(e));
  },
};

/** „If your opponent played a versatile card, deal 2 damage to the opposing fighter." */
const nothingEitherGoodOrBad: CardEffect = {
  after: (e) => {
    if (theyPlayedVersatile(e) && e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, 2);
  },
};

/**
 * „Put any number of cards in your hand on the bottom of your deck. Then,
 * draw up to that many cards. Gain 1 action." Karty sa vyberajú po jednej,
 * `left` nesie, koľko už ich ide pod balíček; preskočenie = hotovo, ťahá sa.
 */
function askMethod(ctx: Ctx, player: PlayerIndex, source: string, done: number): void {
  askChoice(ctx, {
    kind: 'card',
    player,
    source,
    label: `Method in the Madness: daj kartu pod balíček (zatiaľ ${done}) — alebo dosť a ťahaj`,
    optional: true,
    from: 'hand',
    purpose: 'effect',
    except: [source],
    left: done,
    onSkip: { kind: 'effect', effect: METHOD, data: String(done) },
  });
}

const methodInTheMadness: CardEffect = {
  scheme: (e) => {
    gainAction(e.ctx, e.player);
    askMethod(e.ctx, e.player, e.cardId, 0);
  },
  onCardChosen: (ctx, choice, cardId) => {
    handToDeckBottom(ctx, choice.player, cardId);
    askMethod(ctx, choice.player, choice.source ?? METHOD, (choice.left ?? 0) + 1);
  },
  onFollow: (ctx, choice, count) => {
    for (let i = 0; i < Number(count); i++) drawCard(ctx, choice.player);
  },
};

/** „Choose one: Hamlet recovers 2 health / place Hamlet in any space in his zone and gain 1 action." */
const theGhost: CardEffect = {
  scheme: (e) => {
    const follow = (data: string) => ({ kind: 'effect' as const, effect: GHOST, data });
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'The Ghost: vyber jedno',
      optional: false,
      options: [
        { label: 'Hamlet si vylieči 2', follow: follow('heal') },
        { label: 'Hamlet do priestoru v jeho zóne + akcia', follow: follow('place') },
      ],
    });
  },
  onFollow: (ctx, choice, data) => {
    const hamlet = hamletOf(ctx, choice.player);
    if (!hamlet) return;
    if (data === 'heal') {
      healFighter(ctx, hamlet.id, 2);
      return;
    }
    const spaces = emptySpacesInZone(ctx, hamlet.id);
    if (spaces.length > 0) {
      askChoice(ctx, {
        kind: 'place',
        player: choice.player,
        source: choice.source,
        label: 'The Ghost: polož Hamleta v jeho zóne',
        optional: false,
        fighterId: hamlet.id,
        spaces,
      });
    }
    gainAction(ctx, choice.player);
  },
};

export const hamletCards: EffectRegistry = {
  'hamlet.cruel-to-be-kind': cruelToBeKind,
  'hamlet.uncertain-doom': uncertainDoom,
  'hamlet.to-sleep-perchance-to-dream': toSleep,
  [READINESS]: readinessIsAll,
  'hamlet.the-plays-the-thing': playsTheThing,
  'hamlet.blood-will-have-blood': bloodWillHaveBlood,
  [FORTUNE]: outrageousFortune,
  'hamlet.maddening-insight': maddeningInsight,
  'hamlet.the-rest-is-silence': { after: (e) => endTurnNow(e.ctx) },
  'hamlet.nothing-either-good-or-bad': nothingEitherGoodOrBad,
  [METHOD]: methodInTheMadness,
  [GHOST]: theGhost,
};

const notToBe = (ctx: Ctx, player: PlayerIndex) => ctx.state.players[player]?.form === 'notbe';

export const hamletSpecial: HeroSpecial = {
  name: 'The Question',
  /** „At the start of your turn, choose TO BE or NOT TO BE." */
  startOfTurn: (ctx, player) => {
    askChoice(ctx, {
      kind: 'option',
      player,
      source: null,
      label: 'The Question: byť, či nebyť?',
      optional: false,
      options: [
        { label: 'TO BE — pri manévri o kartu viac', follow: { kind: 'special', hero: HAMLET, data: 'be' } },
        { label: 'NOT TO BE — 2 poškodenia vlastnej figúrke, Hamlet útočí +2', follow: { kind: 'special', hero: HAMLET, data: 'notbe' } },
      ],
    });
  },
  onFollow: (ctx, player, data) => {
    const seat = ctx.state.players[player];
    if (!seat) return;
    seat.form = data;
    emit(ctx, { type: 'specialUsed', player, heroId: HAMLET, name: data === 'be' ? 'TO BE' : 'NOT TO BE' });
    if (data !== 'notbe') return;
    const own = ctx.state.fighters.filter((f) => f.owner === player).map((f) => f.id);
    askChoice(ctx, {
      kind: 'damage',
      player,
      source: null,
      label: 'NOT TO BE: 2 poškodenia jednej z tvojich figúrok',
      optional: false,
      amount: 2,
      targets: own,
    });
  },
  /** TO BE: „When you maneuver, draw 1 additional card." */
  maneuverDraw: (ctx, player) => (ctx.state.players[player]?.form === 'be' ? 1 : 0),
  /** NOT TO BE: „Add +2 to the value of Hamlet's attacks." */
  attackModifier: (ctx, player, fighterId) =>
    notToBe(ctx, player) && fighterOrNull(ctx, fighterId)?.defId === HAMLET ? 2 : 0,
};

